import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { routeModule, permCan } from "@/lib/constants";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/components/AppLayout";
import { Loader } from "@/components/ui-kit";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Prospects from "@/pages/Prospects";
import Clients from "@/pages/Clients";
import ClientProjects from "@/pages/ClientProjects";
import Opportunities from "@/pages/Opportunities";
import Quotes from "@/pages/Quotes";
import Prestations from "@/pages/Prestations";
import Invoices from "@/pages/Invoices";
import Suppliers from "@/pages/Suppliers";
import Warehouses from "@/pages/Warehouses";
import Articles from "@/pages/Articles";
import Movements from "@/pages/Movements";
import Purchases from "@/pages/Purchases";
import PurchaseOrders from "@/pages/PurchaseOrders";
import Ouvriers from "@/pages/Ouvriers";
import Creances from "@/pages/Creances";
import Dettes from "@/pages/Dettes";
import Inventories from "@/pages/Inventories";
import Deliveries from "@/pages/Deliveries";
import Contracts from "@/pages/Contracts";
import ContractTemplates from "@/pages/ContractTemplates";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Tasks from "@/pages/Tasks";
import Agenda from "@/pages/Agenda";
import Employees from "@/pages/Employees";
import DailyReports from "@/pages/DailyReports";
import Artisans from "@/pages/Artisans";
import Pointage from "@/pages/Pointage";
import Timesheets from "@/pages/Timesheets";
import Leaves from "@/pages/Leaves";
import Documents from "@/pages/Documents";
import Reports from "@/pages/Reports";
import Notifications from "@/pages/Notifications";
import Users from "@/pages/Users";
import SettingsPage from "@/pages/Settings";
import Treasury from "@/pages/Treasury";
import ValidationDashboard from "@/pages/validation/ValidationDashboard";
import ValidationList from "@/pages/validation/ValidationList";
import ValidationDetail from "@/pages/validation/ValidationDetail";
import ValidationCircuits from "@/pages/validation/ValidationCircuits";
import PartnerDashboard from "@/pages/partner/PartnerDashboard";
import PartnerContacts from "@/pages/partner/PartnerContacts";
import PartnerList from "@/pages/partner/PartnerList";
import PartnerPipelines from "@/pages/partner/PartnerPipelines";
import PartnerDetail from "@/pages/partner/PartnerDetail";
import PartnerActions from "@/pages/partner/PartnerActions";
import PartnerRecords from "@/pages/partner/PartnerRecords";
import PartnerSettings from "@/pages/partner/PartnerSettings";
import FundRequests from "@/pages/FundRequests";
import Finance from "@/pages/Finance";
import Charges from "@/pages/Charges";
import Taxes from "@/pages/Taxes";
import Contributions from "@/pages/Contributions";
import ContractsRH from "@/pages/ContractsRH";
import PayrollElements from "@/pages/PayrollElements";
import Payslips from "@/pages/Payslips";
import PayrollDashboard from "@/pages/PayrollDashboard";
import Courrier from "@/pages/Courrier";
import VerifyLetter from "@/pages/VerifyLetter";
import VerifyOperation from "@/pages/VerifyOperation";
import ValidateByEmail from "@/pages/ValidateByEmail";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import LettersList from "@/pages/letters/LettersList";
import LetterEditor from "@/pages/letters/LetterEditor";
import LetterView from "@/pages/letters/LetterView";
import LetterTemplates from "@/pages/letters/LetterTemplates";
import LetterSettings from "@/pages/letters/LetterSettings";
import Appels from "@/pages/Appels";
import Visiteurs from "@/pages/Visiteurs";
import Messagerie from "@/pages/Messagerie";
import Campagnes from "@/pages/Campagnes";
import AccountingDashboard from "@/pages/AccountingDashboard";
import ChartOfAccounts from "@/pages/ChartOfAccounts";
import Journals from "@/pages/Journals";
import FiscalYears from "@/pages/FiscalYears";
import VatRates from "@/pages/VatRates";
import AccountingEntries from "@/pages/AccountingEntries";
import AccountingStates from "@/pages/AccountingStates";
import AutoGeneration from "@/pages/AutoGeneration";
import AccountingMapping from "@/pages/AccountingMapping";
import Resources from "@/pages/Resources";
import ViDashboard from "@/pages/vi/ViDashboard";
import ViTricycles from "@/pages/vi/ViTricycles";
import { ViCircuits, ViTours } from "@/pages/vi/ViCircuitsTours";
import ViTourDetail from "@/pages/vi/ViTourDetail";
import ViMyTour from "@/pages/vi/ViMyTour";
import { ViSales, ViPayments, ViExpenses, ViReturns, ViAnomalies, ViReports, ViStock, ViClients, ViSettings } from "@/pages/vi/ViPages";
import { ViOrders, ViVisits, ViQuotes, ViObjectives, ViCommissions } from "@/pages/vi/ViPages2";
import { ViProfitability, ViRecommendations } from "@/pages/vi/ViPages3";
import { ViScanClient, ViScanTricycle, ViConflicts } from "@/pages/vi/ViScan";
import ViLiveMap from "@/pages/vi/ViLiveMap";
import ViGpsAlerts from "@/pages/vi/ViGpsAlerts";
import ViKmReport from "@/pages/vi/ViKmReport";
import TechDashboard from "@/pages/technique/TechDashboard";
import TechFile from "@/pages/technique/TechFile";
import TechSuivi from "@/pages/technique/TechSuivi";
import PortalApp from "@/pages/portail/PortalApp";
import PortalAdmin from "@/pages/PortalAdmin";
import Reconciliation from "@/pages/Reconciliation";
import Immobilisations from "@/pages/Immobilisations";
import VatDeclaration from "@/pages/VatDeclaration";
import Lettrage from "@/pages/Lettrage";
import Support from "@/pages/Support";
import { IS_PORTAL_HOST } from "@/lib/portalHost";

function Protected({ children, roles }) {
  const { user, role, permissions } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-slate-100"><Loader /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(role)) return <Navigate to="/" replace />;
  const mod = routeModule(loc.pathname);
  if (mod && !permCan(permissions, mod, "read")) return <Navigate to="/" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function AppRoutes() {
  if (IS_PORTAL_HOST) return <Routes><Route path="/*" element={<PortalApp />} /></Routes>;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
      <Route path="/reinitialiser-mot-de-passe/:token" element={<ResetPassword />} />
      <Route path="/portail/*" element={<PortalApp />} />
      <Route path="/portail-admin" element={<Protected><PortalAdmin /></Protected>} />
      <Route path="/verifier" element={<VerifyLetter />} />
      <Route path="/verifier-operation/:code" element={<VerifyOperation />} />
      <Route path="/valider/:token" element={<ValidateByEmail />} />
      <Route path="/verifier/:code" element={<VerifyLetter />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/prospects" element={<Protected><Prospects /></Protected>} />
      <Route path="/clients" element={<Protected><Clients /></Protected>} />
      <Route path="/projets-clients" element={<Protected><ClientProjects /></Protected>} />
      <Route path="/opportunites" element={<Protected><Opportunities /></Protected>} />
      <Route path="/devis" element={<Protected><Quotes /></Protected>} />
      <Route path="/prestations" element={<Protected><Prestations /></Protected>} />
      <Route path="/artisans" element={<Protected><Artisans /></Protected>} />
      <Route path="/factures" element={<Protected><Invoices /></Protected>} />
      <Route path="/fournisseurs" element={<Protected><Suppliers /></Protected>} />
      <Route path="/articles" element={<Protected><Articles /></Protected>} />
      <Route path="/magasins" element={<Protected><Warehouses /></Protected>} />
      <Route path="/mouvements-stock" element={<Protected><Movements /></Protected>} />
      <Route path="/achats" element={<Protected><Purchases /></Protected>} />
      <Route path="/commandes" element={<Protected><PurchaseOrders /></Protected>} />
      <Route path="/ouvriers" element={<Protected><Ouvriers /></Protected>} />
      <Route path="/creances" element={<Protected><Creances /></Protected>} />
      <Route path="/dettes" element={<Protected><Dettes /></Protected>} />
      <Route path="/inventaire" element={<Protected><Inventories /></Protected>} />
      <Route path="/livraisons" element={<Protected><Deliveries /></Protected>} />
      <Route path="/contrats" element={<Protected><Contracts /></Protected>} />
      <Route path="/modeles-contrats" element={<Protected><ContractTemplates /></Protected>} />
      <Route path="/projets" element={<Protected><Projects /></Protected>} />
      <Route path="/projets/:id" element={<Protected><ProjectDetail /></Protected>} />
      <Route path="/taches" element={<Protected><Tasks /></Protected>} />
      <Route path="/agenda" element={<Protected><Agenda /></Protected>} />
      <Route path="/collaborateurs" element={<Protected><Employees /></Protected>} />
      <Route path="/rapports-collaborateurs" element={<Protected><DailyReports /></Protected>} />
      <Route path="/pointage" element={<Protected><Pointage /></Protected>} />
      <Route path="/feuilles-temps" element={<Protected><Timesheets /></Protected>} />
      <Route path="/conges" element={<Protected><Leaves /></Protected>} />
      <Route path="/tresorerie" element={<Protected><Treasury /></Protected>} />
      <Route path="/validations" element={<Protected><ValidationDashboard /></Protected>} />
      <Route path="/validations/en-attente" element={<Protected><ValidationList scope="pending" /></Protected>} />
      <Route path="/validations/effectuees" element={<Protected><ValidationList scope="done" /></Protected>} />
      <Route path="/validations/mes-demandes" element={<Protected><ValidationList scope="submitted" /></Protected>} />
      <Route path="/validations/toutes" element={<Protected><ValidationList scope="all" /></Protected>} />
      <Route path="/validations/historique" element={<Protected><ValidationList scope="history" /></Protected>} />
      <Route path="/validations/circuits" element={<Protected><ValidationCircuits /></Protected>} />
      <Route path="/validations/demande/:id" element={<Protected><ValidationDetail /></Protected>} />
      <Route path="/partenariat" element={<Protected><PartnerDashboard /></Protected>} />
      <Route path="/partenariat/contacts" element={<Protected><PartnerContacts /></Protected>} />
      <Route path="/partenariat/partenaires" element={<Protected><PartnerList /></Protected>} />
      <Route path="/partenariat/pipelines" element={<Protected><PartnerPipelines /></Protected>} />
      <Route path="/partenariat/fiche/:id" element={<Protected><PartnerDetail /></Protected>} />
      <Route path="/partenariat/actions" element={<Protected><PartnerActions kind="" /></Protected>} />
      <Route path="/partenariat/relances" element={<Protected><PartnerActions kind="relances" /></Protected>} />
      <Route path="/partenariat/reunions" element={<Protected><PartnerActions kind="reunions" /></Protected>} />
      <Route path="/partenariat/contrats" element={<Protected><PartnerRecords kind="contracts" /></Protected>} />
      <Route path="/partenariat/opportunites" element={<Protected><PartnerRecords kind="opps" /></Protected>} />
      <Route path="/partenariat/rapports" element={<Protected><PartnerDashboard reports /></Protected>} />
      <Route path="/partenariat/parametres" element={<Protected><PartnerSettings /></Protected>} />
      <Route path="/demandes-fonds" element={<Protected><FundRequests /></Protected>} />
      <Route path="/finance" element={<Protected><Finance /></Protected>} />
      <Route path="/charges" element={<Protected><Charges /></Protected>} />
      <Route path="/impots" element={<Protected><Taxes /></Protected>} />
      <Route path="/cotisations" element={<Protected><Contributions /></Protected>} />
      <Route path="/paie" element={<Protected><PayrollDashboard /></Protected>} />
      <Route path="/contrats-travail" element={<Protected><ContractsRH /></Protected>} />
      <Route path="/elements-paie" element={<Protected><PayrollElements /></Protected>} />
      <Route path="/bulletins" element={<Protected><Payslips /></Protected>} />
      <Route path="/courrier" element={<Protected><Courrier /></Protected>} />
      <Route path="/courrier/recus" element={<Protected><Courrier sens="Entrant" /></Protected>} />
      <Route path="/courrier/envoyes" element={<Protected><Courrier sens="Sortant" /></Protected>} />
      <Route path="/courriers" element={<Protected><LettersList /></Protected>} />
      <Route path="/courriers/nouveau" element={<Protected><LetterEditor /></Protected>} />
      <Route path="/courriers/modeles" element={<Protected><LetterTemplates /></Protected>} />
      <Route path="/courriers/parametres" element={<Protected><LetterSettings /></Protected>} />
      <Route path="/courriers/:id" element={<Protected><LetterView /></Protected>} />
      <Route path="/courriers/:id/modifier" element={<Protected><LetterEditor /></Protected>} />
      <Route path="/appels" element={<Protected><Appels /></Protected>} />
      <Route path="/visiteurs" element={<Protected><Visiteurs /></Protected>} />
      <Route path="/messagerie" element={<Protected><Messagerie /></Protected>} />
      <Route path="/campagnes" element={<Protected><Campagnes /></Protected>} />
      <Route path="/comptabilite" element={<Protected><AccountingDashboard /></Protected>} />
      <Route path="/ecritures" element={<Protected><AccountingEntries /></Protected>} />
      <Route path="/plan-comptable" element={<Protected><ChartOfAccounts /></Protected>} />
      <Route path="/journaux" element={<Protected><Journals /></Protected>} />
      <Route path="/exercices" element={<Protected><FiscalYears /></Protected>} />
      <Route path="/tva" element={<Protected><VatRates /></Protected>} />
      <Route path="/etats-comptables" element={<Protected><AccountingStates /></Protected>} />
      <Route path="/generation-comptable" element={<Protected><AutoGeneration /></Protected>} />
      <Route path="/mapping-comptable" element={<Protected><AccountingMapping /></Protected>} />
      <Route path="/formation" element={<Protected><Resources /></Protected>} />
      <Route path="/guides" element={<Protected><Resources /></Protected>} />
      <Route path="/vi" element={<Protected><ViDashboard /></Protected>} />
      <Route path="/vi/ma-tournee" element={<Protected><ViMyTour /></Protected>} />
      <Route path="/vi/tournees" element={<Protected><ViTours /></Protected>} />
      <Route path="/vi/tournees/:id" element={<Protected><ViTourDetail /></Protected>} />
      <Route path="/vi/tricycles" element={<Protected><ViTricycles /></Protected>} />
      <Route path="/vi/circuits" element={<Protected><ViCircuits /></Protected>} />
      <Route path="/vi/clients" element={<Protected><ViClients /></Protected>} />
      <Route path="/vi/stock" element={<Protected><ViStock /></Protected>} />
      <Route path="/vi/ventes" element={<Protected><ViSales /></Protected>} />
      <Route path="/vi/encaissements" element={<Protected><ViPayments /></Protected>} />
      <Route path="/vi/retours" element={<Protected><ViReturns /></Protected>} />
      <Route path="/vi/depenses" element={<Protected><ViExpenses /></Protected>} />
      <Route path="/vi/inventaires" element={<Protected><ViAnomalies /></Protected>} />
      <Route path="/vi/rapports" element={<Protected><ViReports /></Protected>} />
      <Route path="/vi/parametres" element={<Protected><ViSettings /></Protected>} />
      <Route path="/vi/commandes" element={<Protected><ViOrders /></Protected>} />
      <Route path="/vi/visites" element={<Protected><ViVisits /></Protected>} />
      <Route path="/vi/devis" element={<Protected><ViQuotes /></Protected>} />
      <Route path="/vi/objectifs" element={<Protected><ViObjectives /></Protected>} />
      <Route path="/vi/commissions" element={<Protected><ViCommissions /></Protected>} />
      <Route path="/vi/rentabilite" element={<Protected><ViProfitability /></Protected>} />
      <Route path="/vi/a-visiter" element={<Protected><ViRecommendations /></Protected>} />
      <Route path="/vi/scan/c/:id" element={<Protected><ViScanClient /></Protected>} />
      <Route path="/vi/scan/t/:id" element={<Protected><ViScanTricycle /></Protected>} />
      <Route path="/vi/conflits" element={<Protected><ViConflicts /></Protected>} />
      <Route path="/vi/carte" element={<Protected><ViLiveMap /></Protected>} />
      <Route path="/vi/alertes-gps" element={<Protected><ViGpsAlerts /></Protected>} />
      <Route path="/vi/rapport-km" element={<Protected><ViKmReport /></Protected>} />
      <Route path="/technique" element={<Protected><TechDashboard /></Protected>} />
      <Route path="/technique/suivi" element={<Protected><TechSuivi /></Protected>} />
      <Route path="/technique/dossier/:id" element={<Protected><TechFile /></Protected>} />
      <Route path="/rapprochement" element={<Protected><Reconciliation /></Protected>} />
      <Route path="/immobilisations" element={<Protected><Immobilisations /></Protected>} />
      <Route path="/declaration-tva" element={<Protected><VatDeclaration /></Protected>} />
      <Route path="/lettrage" element={<Protected><Lettrage /></Protected>} />
      <Route path="/documents" element={<Protected><Documents /></Protected>} />
      <Route path="/rapports" element={<Protected><Reports /></Protected>} />
      <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
      <Route path="/support" element={<Protected><Support /></Protected>} />
      <Route path="/utilisateurs" element={<Protected><Users /></Protected>} />
      <Route path="/parametres" element={<Protected><SettingsPage /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="bottom-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
